import { useEffect, useState, useRef } from "react";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import { Upload, Palette, Save, Lock, CheckCircle, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BrandingPage() {
  const [branding, setBranding] = useState(null);
  const [allowed, setAllowed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/my/branding").then(r => { setBranding(r.data.branding); setAllowed(r.data.branding_allowed); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

  const save = async () => {
    try {
      await api.put("/my/branding", branding);
      flash("success", "Branding saved — applied to PDFs next time you export");
    } catch (e) { flash("error", formatError(e)); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("sage_token");
      const res = await fetch(`${API}/api/my/branding/logo`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) { const j = await res.json(); throw new Error(j.detail || "Upload failed"); }
      const j = await res.json();
      setBranding(b => ({ ...b, logo_url: j.logo_url }));
      flash("success", "Logo uploaded");
    } catch (err) { flash("error", err.message); }
  };

  if (loading) return <div className="min-h-screen bg-cream"><Navbar /><div className="text-center pt-20 text-sage-500">Loading…</div></div>;

  if (!allowed) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <main className="max-w-2xl mx-auto px-6 py-10 text-center">
          <div className="bg-white border border-[#E2E8E3] rounded-md p-8">
            <Lock className="w-10 h-10 text-sage-400 mx-auto mb-3" />
            <h1 className="font-heading text-xl font-semibold text-sage-900 mb-1">Whitelabel not included</h1>
            <p className="text-sage-500 text-sm mb-4">Upgrade to Starter or above to brand all PDF reports with your logo and colours.</p>
            <a href="/pricing" className="inline-block bg-sage-600 text-white font-semibold text-sm rounded-md px-4 py-2 hover:bg-sage-700">See plans</a>
          </div>
        </main>
      </div>
    );
  }

  const logoSrc = branding.logo_url ? (branding.logo_url.startsWith("http") ? branding.logo_url : `${API}${branding.logo_url}`) : null;

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Whitelabel</p>
          <h1 className="font-heading text-2xl font-semibold text-sage-900 flex items-center gap-2"><Palette className="w-6 h-6 text-sage-600" /> Brand Settings</h1>
          <p className="text-sage-500 text-sm mt-1">Applied to every PDF export and share-link page.</p>
        </div>

        {msg && <div className={`mb-4 flex items-center gap-2 text-sm rounded-md px-3 py-2 ${msg.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{msg.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />} {msg.text}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E2E8E3] rounded-md p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Firm name</label>
              <input data-testid="branding-firm-name" type="text" value={branding.firm_name || ""} onChange={e => setBranding({ ...branding, firm_name: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" placeholder="e.g. Acme Lean Studio" />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Logo (PNG/JPG/WEBP)</label>
              <div className="flex items-center gap-3">
                {logoSrc ? <img src={logoSrc} alt="logo" className="w-14 h-14 object-contain border border-sage-100 rounded" /> : <div className="w-14 h-14 border border-dashed border-sage-200 rounded flex items-center justify-center text-sage-400 text-xs">None</div>}
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="branding-logo-input" />
                <button data-testid="branding-upload-button" onClick={() => fileRef.current?.click()} className="border border-sage-200 text-sage-700 rounded-md px-3 py-2 text-sm font-semibold hover:bg-sage-50 flex items-center gap-2"><Upload className="w-4 h-4" /> Upload</button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Primary colour</label>
                <input data-testid="branding-primary-color" type="color" value={branding.primary_color || "#4B5E4D"} onChange={e => setBranding({ ...branding, primary_color: e.target.value })} className="w-full h-10 border border-sage-200 rounded-md" />
                <p className="text-xs text-sage-500 mt-1 font-mono">{branding.primary_color}</p>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Accent colour</label>
                <input data-testid="branding-accent-color" type="color" value={branding.accent_color || "#7C9A7E"} onChange={e => setBranding({ ...branding, accent_color: e.target.value })} className="w-full h-10 border border-sage-200 rounded-md" />
                <p className="text-xs text-sage-500 mt-1 font-mono">{branding.accent_color}</p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1.5">Footer text</label>
              <input data-testid="branding-footer-text" type="text" value={branding.footer_text || ""} onChange={e => setBranding({ ...branding, footer_text: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm focus:border-sage-500 outline-none" />
            </div>

            <label className="flex items-center gap-2 text-sm text-sage-700">
              <input type="checkbox" data-testid="branding-hide-powered-by" checked={!!branding.hide_powered_by} onChange={e => setBranding({ ...branding, hide_powered_by: e.target.checked })} />
              Hide "Powered by TheKaizenCabinet" footer on PDFs
            </label>

            <button data-testid="branding-save-button" onClick={save} className="w-full bg-sage-600 text-white rounded-md py-2 text-sm font-semibold hover:bg-sage-700 flex items-center justify-center gap-2"><Save className="w-4 h-4" /> Save branding</button>
          </div>

          <div className="bg-white border border-[#E2E8E3] rounded-md p-5" data-testid="branding-preview">
            <h3 className="font-heading text-sm font-semibold text-sage-600 uppercase tracking-wider mb-3">PDF cover preview</h3>
            <div className="border border-sage-100 rounded overflow-hidden shadow-sm">
              <div className="h-10 flex items-center px-4 text-white text-xs font-semibold" style={{ backgroundColor: branding.primary_color || "#4B5E4D" }}>
                {logoSrc && <img src={logoSrc} alt="" className="h-6 mr-2 bg-white/10 rounded p-0.5" />}
                {branding.firm_name || "TheKaizenCabinet"} · Lean Toolbox
              </div>
              <div className="bg-[#FAFAF7] p-6 text-center">
                <p className="text-xs font-semibold text-sage-500 uppercase tracking-wider mb-1">Sample report</p>
                <h3 className="font-heading text-lg font-bold" style={{ color: branding.primary_color }}>Lean Diagnostic</h3>
                <p className="text-xs text-sage-500 mt-1">Acme Corporation · Operations</p>
                <div className="mt-3 h-0.5 w-20 mx-auto rounded" style={{ backgroundColor: branding.accent_color || "#7C9A7E" }} />
              </div>
              <div className="bg-white p-3 text-xs text-sage-500 flex items-center justify-between border-t border-sage-100">
                <span>{branding.footer_text || "Confidential"}</span>
                {!branding.hide_powered_by && <span className="text-sage-400 text-[10px]">Powered by TheKaizenCabinet</span>}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
